import React, { useState } from "react";
import { Accordion, Icon, Label, Table, Message, Segment, Input, Checkbox, Button, List } from "semantic-ui-react";
import { casosDeTipologia } from "../catalogs/casosDeCampo";
import { coberturaDeCaso } from "../adaptadorAgua";
import { calcularCasoA2, calcularCasoCaldera, calcularCasoB1, calcularCasoB2, calcularCasoC1, calcularDescuadre, lecturaValida, gradoConEvidencia } from "../waterBalanceCalc";
import { useWaterBalanceT } from "../useWaterBalanceT";

// Traduce las variables genéricas de la memoria de cálculo (catalogs/casosDeCampo.js)
// a los parámetros con nombre que espera cada función del motor. Es glue de
// UI: la aritmética sigue viviendo únicamente en waterBalanceCalc.js.
//
// A1 y C2 no tienen memoria de cálculo propia (A1 es lectura directa, C2 usa
// la misma ecuación de cierre del paso Cierre): igual se registran acá,
// porque A1 es el único caso con grado 5 y es el que necesita evidencia real
// para sostenerlo (Fuentes.md §5: "el grado 5 exige evidencia adjunta, y hoy
// no se adjunta nada" — esto lo resuelve).
const CALCULADORAS = {
  A1_POZO_MACROMEDIDOR: {
    campos: [
      { key: "LECTURA_INICIAL", label: "Lectura inicial del totalizador (m³)" },
      { key: "LECTURA_FINAL", label: "Lectura final del totalizador (m³)" },
      { key: "VOLUMEN_AUTORIZADO_PERMISO", label: "Volumen autorizado en el permiso (m³) — se carga una sola vez" },
    ],
    ejecutar: (v) => {
      const inicial = Number(v.LECTURA_INICIAL) || 0;
      const final = Number(v.LECTURA_FINAL) || 0;
      if (!lecturaValida(inicial, final)) {
        return { error: "La lectura final es menor que la inicial. ¿Se cambió el medidor sin registrar el corte?" };
      }
      const volumenM3 = final - inicial;
      const permiso = Number(v.VOLUMEN_AUTORIZADO_PERMISO) || 0;
      const usoDelPermisoPct = permiso ? +((volumenM3 / permiso) * 100).toFixed(1) : null;
      return { volumenM3, usoDelPermisoPct };
    },
    resultado: (r) => {
      if (r.error) return [r.error];
      const linea1 = `Volumen del período = ${r.volumenM3.toLocaleString("es-AR")} m³`;
      if (r.usoDelPermisoPct == null) return [linea1, "Cargá el volumen autorizado del permiso para ver el % de uso."];
      // V12/V13: >100% es alerta legal de severidad alta (requiere
      // documentar la causa), distinta del aviso preventivo de ≥90%.
      if (r.usoDelPermisoPct > 100) {
        return [linea1, `Uso del permiso = ${r.usoDelPermisoPct}% — ALERTA LEGAL de severidad alta: la extracción supera el volumen autorizado del permiso. Documentá la causa antes de presentar el balance.`];
      }
      if (r.usoDelPermisoPct >= 90) {
        return [linea1, `Uso del permiso = ${r.usoDelPermisoPct}% — aviso preventivo: por encima del 90%.`];
      }
      return [linea1, `Uso del permiso = ${r.usoDelPermisoPct}%`];
    },
    // V12: la alerta legal por sobre-extracción se marca como "stop", no
    // como éxito, aunque el cálculo en sí no tenga error.
    esAlertaLegal: (r) => !r.error && r.usoDelPermisoPct > 100,
  },
  A2_TORRE_POR_DIFERENCIA: {
    campos: [
      { key: "REPOSICION_MEDIDA", label: "Reposición medida (m³)" },
      { key: "PURGA_MEDIDA", label: "Purga medida (m³)" },
      { key: "REPOSICION_VENCIDA", label: "Calibración de la reposición vencida o faltante", tipo: "boolean" },
      { key: "PURGA_VENCIDA", label: "Calibración de la purga vencida o faltante", tipo: "boolean" },
    ],
    // Motor de cálculo §6: un valor derivado de dos mediciones hereda el
    // problema del peor de los dos medidores — si alguno tiene la
    // calibración vencida, el grado baja a 2, no se queda en el 3 de "resta
    // entre dos medidores que sí miden".
    calibracionVencidaKeys: ["REPOSICION_VENCIDA", "PURGA_VENCIDA"],
    ejecutar: (v) => calcularCasoA2({ reposicionMedida: Number(v.REPOSICION_MEDIDA) || 0, purgaMedida: Number(v.PURGA_MEDIDA) || 0 }),
    resultado: (r) => [`Evaporación = ${r.evaporacionM3.toLocaleString("es-AR")} m³`, `Ciclos de concentración = ${r.ciclosDeConcentracion}`],
  },
  A3_CALDERA_POR_DIFERENCIA: {
    campos: [
      { key: "REPOSICION_MEDIDA", label: "Reposición medida (m³)" },
      { key: "PURGA_MEDIDA", label: "Purga medida (m³)" },
      { key: "REPOSICION_VENCIDA", label: "Calibración de la reposición vencida o faltante", tipo: "boolean" },
      { key: "PURGA_VENCIDA", label: "Calibración de la purga vencida o faltante", tipo: "boolean" },
    ],
    // Mismo principio que A2 (motor de cálculo §5/§6): derivado de dos
    // mediciones, grado 3; si alguna calibración está vencida, grado 2.
    calibracionVencidaKeys: ["REPOSICION_VENCIDA", "PURGA_VENCIDA"],
    ejecutar: (v) => calcularCasoCaldera({ reposicionMedida: Number(v.REPOSICION_MEDIDA) || 0, purgaMedida: Number(v.PURGA_MEDIDA) || 0 }),
    resultado: (r) => [`Vapor perdido = ${r.vaporM3.toLocaleString("es-AR")} m³ (la purga vuelve al efluente y ya se mide ahí: no se cuenta dos veces)`],
  },
  B1_MEDIDOR_COMPARTIDO: {
    campos: [
      { key: "MEDIDOR", label: "Lectura del medidor (m³) — techo" },
      { key: "CARGAS", label: "Cargas del período" },
      { key: "KG_POR_CARGA", label: "Kg por carga" },
      { key: "RELACION_DE_BANO", label: "Relación de baño (L/kg)" },
      { key: "PERSONAS", label: "Personas presentes" },
      { key: "DIAS_OPERATIVOS", label: "Días operativos" },
      { key: "LITROS_POR_PERSONA_DIA", label: "Litros por persona-día" },
    ],
    ejecutar: (v) =>
      calcularCasoB1({
        medidorM3: Number(v.MEDIDOR) || 0,
        cargas: Number(v.CARGAS) || 0,
        kgPorCarga: Number(v.KG_POR_CARGA) || 0,
        relacionDeBanoLKg: Number(v.RELACION_DE_BANO) || 0,
        personas: Number(v.PERSONAS) || 0,
        diasOperativos: Number(v.DIAS_OPERATIVOS) || 0,
        litrosPorPersonaDia: Number(v.LITROS_POR_PERSONA_DIA) || 0,
      }),
    resultado: (r) => [
      `Tintura = ${r.tinturaM3.toFixed(1)} m³`,
      `Doméstico = ${r.domesticoM3.toFixed(1)} m³`,
      `No asignado = ${r.noAsignadoM3.toFixed(1)} m³ — no se reparte, se clasifica en el paso de Cierre (M36).`,
    ],
  },
  B2_VARIACION_INVENTARIO: {
    campos: [{ key: "DIAMETRO", label: "Diámetro del tanque (m)" }, { key: "NIVEL_INICIAL", label: "Nivel inicial (m)" }, { key: "NIVEL_FINAL", label: "Nivel final (m)" }],
    ejecutar: (v) => calcularCasoB2({ diametro: Number(v.DIAMETRO) || 0, nivelInicial: Number(v.NIVEL_INICIAL) || 0, nivelFinal: Number(v.NIVEL_FINAL) || 0 }),
    resultado: (r) => [`Δ inventario = ${r.deltaInventarioM3.toFixed(2)} m³ (el signo entra en la ecuación Entradas = Salidas + Δ)`],
  },
  C1_LAVANDERIA_RELACION_BANO: {
    campos: [{ key: "PRENDAS", label: "Prendas procesadas" }, { key: "PESO_MEDIO_POR_PRENDA", label: "Peso medio por prenda (kg)" }, { key: "SUMA_RELACION_DE_BANO_DE_CADA_BANO", label: "Suma de relación de baño de todos los baños (L/kg)" }],
    ejecutar: (v) => calcularCasoC1({ prendas: Number(v.PRENDAS) || 0, pesoMedioPorPrendaKg: Number(v.PESO_MEDIO_POR_PRENDA) || 0, relacionDeBanoAcumuladaLKg: Number(v.SUMA_RELACION_DE_BANO_DE_CADA_BANO) || 0 }),
    resultado: (r) => [`Kg procesados = ${r.kgProcesados.toLocaleString("es-AR")}`, `Volumen = ${r.volumenM3.toFixed(2)} m³`],
  },
  C2_CIERRE_FACTURA_Y_TANQUE: {
    campos: [
      { key: "ENTRADAS", label: "Total facturado del período (m³)" },
      { key: "SALIDAS", label: "Total de salidas medidas/estimadas (m³)" },
      { key: "DELTA_INVENTARIO", label: "Δ inventario (m³, con signo)" },
    ],
    ejecutar: (v) => calcularDescuadre({ totalEntradas: Number(v.ENTRADAS) || 0, totalSalidas: Number(v.SALIDAS) || 0, deltaInventarioM3: Number(v.DELTA_INVENTARIO) || 0 }),
    resultado: (r) => [`Descuadre = ${r.descuadre.toLocaleString("es-AR")} m³ · ${r.descuadrePct.toFixed(1)}%`, "El semáforo y el bloqueo por clasificación se ven en el paso de Cierre."],
  },
};

function FichaEjecucion({ caso, onEjecutarCaso, onUploadEvidencia }) {
  const t = useWaterBalanceT();
  const calc = CALCULADORAS[caso.id];
  const [valores, setValores] = useState({});
  const [resultado, setResultado] = useState(null);
  const [evidencia, setEvidencia] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [guardado, setGuardado] = useState(false);
  if (!calc) return null;

  const calcular = () => {
    setResultado(calc.ejecutar(valores));
    setGuardado(false);
  };

  const subirArchivo = async (file) => {
    if (!file || !onUploadEvidencia) return;
    setSubiendo(true);
    try {
      const subido = await onUploadEvidencia(file);
      if (subido) setEvidencia((prev) => [...prev, subido]);
    } finally {
      setSubiendo(false);
    }
  };

  const derivadoConMedidorVencido = (calc.calibracionVencidaKeys || []).some((k) => valores[k]);
  const gradoEfectivo = derivadoConMedidorVencido
    ? Math.min(gradoConEvidencia(caso.gradoOtorgado, evidencia.length > 0), 2)
    : gradoConEvidencia(caso.gradoOtorgado, evidencia.length > 0);

  const guardarEnBitacora = () => {
    onEjecutarCaso({
      caseId: caso.id,
      date: new Date().toISOString(),
      responsible: caso.quien.join(", "),
      data: { ...valores, ...resultado },
      gradeAwarded: gradoEfectivo,
      durationMin: caso.duracionMin,
      evidence: evidencia,
    });
    setGuardado(true);
  };

  return (
    <Segment>
      <h5>{calc === CALCULADORAS.A1_POZO_MACROMEDIDOR || calc === CALCULADORAS.C2_CIERRE_FACTURA_Y_TANQUE ? t("water_balance_execution_record") : t("water_balance_calculation_memory")}</h5>
      <Table basic="very" compact>
        <Table.Body>
          {calc.campos.map((c) => (
            <Table.Row key={c.key}>
              <Table.Cell>{c.label}</Table.Cell>
              <Table.Cell>
                {c.tipo === "boolean" ? (
                  <Checkbox checked={!!valores[c.key]} onChange={(_, d) => setValores({ ...valores, [c.key]: d.checked })} />
                ) : (
                  <Input size="small" type="number" value={valores[c.key] || ""} onChange={(e) => setValores({ ...valores, [c.key]: e.target.value })} />
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Button primary size="small" onClick={calcular}>
        {t("water_balance_calculate_button")}
      </Button>

      {resultado && (
        <Message
          success={!resultado.error && !calc.esAlertaLegal?.(resultado)}
          negative={!!resultado.error || !!calc.esAlertaLegal?.(resultado)}
          size="small"
          style={{ marginTop: 10 }}
        >
          <List bulleted>
            {calc.resultado(resultado).map((linea, i) => (
              <List.Item key={i}>{linea}</List.Item>
            ))}
          </List>
          {!resultado.error && (
            <>
              <p>{t("water_balance_move_result_hint")}</p>

              <div style={{ margin: "10px 0" }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  {t("water_balance_evidence_label")}
                </label>
                <input type="file" disabled={subiendo} onChange={(e) => subirArchivo(e.target.files?.[0])} />
                {evidencia.length > 0 && (
                  <Label color="green" basic style={{ marginLeft: 8 }}>
                    <Icon name="paperclip" /> {t("water_balance_files_count", { n: evidencia.length })}
                  </Label>
                )}
                {caso.gradoOtorgado === 5 && evidencia.length === 0 && (
                  <p style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }}>
                    {t("water_balance_grade5_no_evidence_warning", { gradoEfectivo, gradoOtorgado: caso.gradoOtorgado })}
                  </p>
                )}
                {derivadoConMedidorVencido && (
                  <p style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }}>{t("water_balance_derived_expired_meter_warning", { gradoEfectivo })}</p>
                )}
              </div>

              <Button size="tiny" positive={!guardado} disabled={guardado} onClick={guardarEnBitacora}>
                {guardado ? t("water_balance_saved_to_log", { grado: gradoEfectivo }) : t("water_balance_save_to_log", { grado: gradoEfectivo })}
              </Button>
            </>
          )}
        </Message>
      )}
    </Segment>
  );
}

// Paso 6 — Fichas de campo. Filtradas por tipología, con el formato único
// §1bis: "ya está en Agua" y "hay que conseguir" arriba de todo, antes que
// "quién lo hace" — la primera pregunta que hay que contestar es si hace
// falta ir a la planta.
export function FichasDeCampoStep({ typology, heredado, executedFieldCases, onChangeCasosEjecutados, onUploadEvidencia }) {
  const t = useWaterBalanceT();
  const [abierta, setAbierta] = useState(null);
  const casos = typology ? casosDeTipologia(typology) : [];

  const registrarEjecucion = (entrada) => onChangeCasosEjecutados([...(executedFieldCases || []), entrada]);

  if (!typology) {
    return <Message warning icon="warning sign" content={t("water_balance_choose_typology_first_cases")} />;
  }

  return (
    <div className="water-balance-step">
      <h3>{t("water_balance_field_cases_title")}</h3>
      <Accordion styled fluid>
        {casos.map((caso) => {
          const cobertura = coberturaDeCaso(caso.id, heredado);
          const abiertaAhora = abierta === caso.id;
          return (
            <React.Fragment key={caso.id}>
              <Accordion.Title active={abiertaAhora} onClick={() => setAbierta(abiertaAhora ? null : caso.id)}>
                <Icon name="dropdown" />
                {caso.nombre}
                <Label style={{ marginLeft: 10 }}>{t("water_balance_grade_label", { grado: caso.gradoOtorgado })}</Label>
                <Label basic style={{ marginLeft: 6 }}>{caso.duracionMin >= 480 ? t("water_balance_one_day") : `${caso.duracionMin} min`}</Label>
                {cobertura && !cobertura.sinHerencia && (
                  <Label color="blue" basic style={{ marginLeft: 6 }}>
                    {t("water_balance_pct_already_in_agua", { pct: cobertura.pctResuelto })}
                  </Label>
                )}
              </Accordion.Title>
              <Accordion.Content active={abiertaAhora}>
                <Message info>
                  <strong>{t("water_balance_already_in_agua")}:</strong> {caso.yaEstaEnAgua}
                </Message>
                <Message warning>
                  <strong>{t("water_balance_need_to_get")}:</strong> {caso.hayQueConseguir}
                </Message>
                {caso.atajo && <Message positive icon="lightbulb" content={caso.atajo} />}

                <Table basic="very" compact>
                  <Table.Body>
                    <Table.Row><Table.Cell width={4}><strong>{t("water_balance_who_does_it")}</strong></Table.Cell><Table.Cell>{caso.quien.join(", ")}</Table.Cell></Table.Row>
                    <Table.Row><Table.Cell><strong>{t("water_balance_where_to_get_it")}</strong></Table.Cell><Table.Cell>{caso.donde}</Table.Cell></Table.Row>
                    <Table.Row><Table.Cell><strong>{t("water_balance_what_to_bring")}</strong></Table.Cell><Table.Cell>{caso.llevar.join(", ") || t("water_balance_nothing_if_meters_exist")}</Table.Cell></Table.Row>
                  </Table.Body>
                </Table>

                {caso.pasos && (
                  <>
                    <h5>{t("water_balance_how_to_obtain")}</h5>
                    <List ordered>{caso.pasos.map((p, i) => <List.Item key={i}>{p}</List.Item>)}</List>
                  </>
                )}

                <FichaEjecucion caso={caso} onEjecutarCaso={registrarEjecucion} onUploadEvidencia={onUploadEvidencia} />

                {caso.erroresFrecuentes && (
                  <Message negative size="small">
                    <strong>{t("water_balance_common_mistakes")}</strong>
                    <List bulleted>{caso.erroresFrecuentes.map((e, i) => <List.Item key={i}>{e}</List.Item>)}</List>
                  </Message>
                )}
              </Accordion.Content>
            </React.Fragment>
          );
        })}
      </Accordion>
    </div>
  );
}

export default FichasDeCampoStep;
